import React, { useState, useContext } from "react";
import CountryContext from "CountryContext";

import {
  createFilterParams,
  createOrderingParams,
} from "../../../components/Tables/utils";
import { tableIcons } from "../../../ui-components/TableIcons";
import TableStyle from "../../../components/TableStyle";
import MaterialTable from "@material-table/core";
import _ from "lodash";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import Badge from "../../../ui-components/Badge";
import WorksiteNameColumn from "components/WorksiteNameColumn";

export const OPTIONS = {
  filtering: true,
  toolbar: false,
  sorting: true,
  emptyRowsWhenPaging: false,
  pageSize: 10,
  pageSizeOptions: [5, 10, 20, 50],
  headerStyle: { fontWeight: "bold", fontSize: 15 },
  filterCellStyle: { paddingTop: 8, maxWidth: 200 },
  cellStyle: { textTransform: "uppercase" },
};

export const LOCALIZATION = {
  header: {
    actions: "",
  },
  pagination: {
    labelDisplayedRows: "{from}-{to} di {count} righe",
    labelRowsSelect: "Righe",
  },
};

const getColumns = (intl, worksiteTypes, services, countryCode) => [
  {
    title: "Link",
    field: "worksite_id",
    filtering: false,
    defaultSort: "desc",
    render: ({ worksite_id }) => (
      <div>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`/${countryCode}/worksites/${worksite_id}`}
        >
          <ArrowTopRightOnSquareIcon
            className={`w-6 h-6 mt-2 text-kerakoll-400 cursor-pointer`}
          />
        </a>
      </div>
    ),
  },
  /*   {
    title: intl.formatMessage({ id: "name" }),
    field: "des_worksite",
    type: "string",
  }, */
  {
    title: intl.formatMessage({ id: "name" }),
    field: "address",
    type: "string",
    headerStyle: { minWidth: 250 },
    cellStyle: { minWidth: 250, textTransform: "uppercase" },
    render: ({ address, des_worksite, city }) => (
      <div className="font-semibold">
        <WorksiteNameColumn
          des_worksite={des_worksite}
          address={address}
          city={city}
        />
      </div>
    ),
  },
  {
    title: intl.formatMessage({ id: "city" }),
    field: "city",
    type: "string",
    headerStyle: { minWidth: 220 },
    cellStyle: { minWidth: 220, textTransform: "uppercase" },
  },
  {
    title: intl.formatMessage({ id: "province_short", defaultMessage: "Prov" }),
    field: "cod_province",
    type: "string",
  },
  {
    title: intl.formatMessage({ id: "type", defaultMessage: "Tipo" }),
    field: "cod_worksite_type",
    lookup: worksiteTypes.reduce(
      (acc, i) => ({ ...acc, [i.cod_worksite_type]: i.des_worksite_type }),
      {}
    ),
    render: ({ cod_worksite_type }) => (
      <div>{intl.formatMessage({ id: cod_worksite_type })}</div>
    ),
  },
  {
    title: intl.formatMessage({
      id: "configurations",
      defaultMessage: "Configurazioni",
    }),
    field: "services",
    lookup: services.reduce((acc, i) => ({ ...acc, [i]: i }), {}),
    render: function (row) {
      return row["services"] ? (
        <div>
          {row["services"].map((s) =>
            s ? (
              <Badge
                classNames="mb-1 mr-1"
                text={s}
                key={s}
                textSize="text-xxs"
              />
            ) : null
          )}
        </div>
      ) : null;
    },
  },
  {
    title: " #",
    field: "num_configurations",
    type: "string",
    filtering: false,
    render: ({ num_configurations }) => (
      <div className="font-semibold text-xl">{num_configurations}</div>
    ),
  },
  {
    title: intl.formatMessage({ id: "value", defaultMessage: "Valore" }),
    field: "amt_worksite",
    type: "string",
    filtering: false,
    render: ({ amt_worksite }) => (
      <div className="font-semibold text-lg">
        {intl.formatNumber(amt_worksite, {
          style: "currency",
          currency: "EUR",
          maximumFractionDigits: 0,
        })}
      </div>
    ),
  },
];

function WorksitesDetailTable({
  intl,
  push,
  countryApiRequest,
  analysisPeriod,
  internalUsers,
  selectedStatus,
  selectedOwnership,
  visibilityUserId,
  worksiteTypes,
  services,
  filtering = true,
  address,
  city,
  cod_province,
  cod_worksite_type,
  filterServices,
}) {
  // state
  const ref = React.useRef(null);
  const [columns, setColumns] = useState([]);
  const {
    country: { code },
  } = useContext(CountryContext);

  const countryCode = code;
  return (
    <>
      <TableStyle>
        <MaterialTable
          tableRef={ref}
          columns={columns}
          data={(query) =>
            new Promise((resolve, reject) => {
              const ordering = createOrderingParams(query);
              const payload = {
                start_date: analysisPeriod.start,
                end_date: analysisPeriod.end,
                visibility_user_id: visibilityUserId,
                internal_users: internalUsers.map((i) => i.id),
                filter_status: selectedStatus.length
                  ? selectedStatus.map((i) => i.value)
                  : undefined,
                filter_ownership: selectedOwnership.length
                  ? selectedOwnership.map((i) => i.value)
                  : undefined,
                ...{ offset: query.page * query.pageSize },
                ...{ limit: query.pageSize },
                ...(ordering && { ordering }),
                ...(address && { address }),
                ...(city && { city }),
                ...(cod_province && { cod_province }),
                ...(cod_worksite_type && { cod_worksite_type }),
                ...(filterServices && { services: filterServices }),
              };
              return countryApiRequest
                .post("/analytics/worksites/detail", payload)
                .then((result) => {
                  if (!columns.length) {
                    setColumns(getColumns(intl, worksiteTypes, services, countryCode));
                  }
                  resolve({
                    data: result["items"],
                    page: query.page,
                    totalCount: result["total"],
                  });
                })
                .catch((err) => {
                  console.log(err);
                  push({
                    title: "Errore durante il caricamento",
                    type: "error",
                  });
                  reject();
                });
            })
          }
          icons={tableIcons}
          options={{ ...OPTIONS, filtering }}
          localization={LOCALIZATION}
        />
      </TableStyle>
    </>
  );
}

export default React.memo(WorksitesDetailTable, (props, nextProps) => {
  // Render component only when users changes
  return (
    _.isEqual(props.analysisPeriod, nextProps.analysisPeriod) &&
    _.isEqual(props.selectedStatus, nextProps.selectedStatus) &&
    _.isEqual(props.selectedOwnership, nextProps.selectedOwnership)
  );
});
